/* eslint-disable no-unsafe-optional-chaining */
import { AccessLevel, ChannelEvent, DeliveryLogType, StatusMessageCategory } from '@dev/ee-api/ee-api-models';
import { flatten, isEmpty } from 'lodash-es';

import { filterStatusesToDeliveryLogTypees, LogStatusesToDisplay } from '@/services/dictionaries/Logs';

const messageCategorySubstrings = ["'Unsubscribed'", "'Complaint'", 'transactional'];
export type MinimalEventInterface = Pick<ChannelEvent, 'Message' | 'EventType' | 'MessageCategory'>;

export const isResendAllowed = (event: MinimalEventInterface): boolean => {
    if (!event.Message) {
        return false;
    }

    if (event.EventType === DeliveryLogType.WaitingToRetry) {
        return false;
    }

    return !(
        event.MessageCategory === 'NotDelivered' &&
        messageCategorySubstrings.some((substring) => event.Message?.includes(substring))
    );
};

/**
 * cause message categories have the same values, and can be assigned to wait to retry and error status, we have to somehow distinguish them, so message category when clicked is passing `{MessageCategory}-{parentStatus}`, and this function retrive it.
 */
export const removeStatusFromMessageCategory = (
    statuses = [],
    messageCategory = [],
    onlyLastEvent = false
): [DeliveryLogType[], StatusMessageCategory[]] => {
    if (!messageCategory || isEmpty(messageCategory)) {
        return [statuses, messageCategory];
    }
    const messageCategoryToReduce = flatten([messageCategory]);

    const [updatedMessaged, statusesToAdd] = messageCategoryToReduce?.reduce(
        ([accMessages, accStatuses], singleMessageCategory) => {
            const [status, message] = singleMessageCategory?.split('_');
            const castedStatus = filterStatusesToDeliveryLogTypees[status];

            const msgCategory = onlyLastEvent ? message : `${castedStatus}_${message}`;

            return [
                [...accMessages, msgCategory],
                [...accStatuses, status],
            ];
        },
        [[], []]
    );

    return [[...new Set(flatten([...statuses, ...statusesToAdd]))] as DeliveryLogType[], updatedMessaged];
};

/**
 * function splits selected event filters from table headers to statuses and message category.
 * as the filter combines two separates api parameters
 */

export const splitSelectedItemsIntoProperState = (
    arrayToSplit: (DeliveryLogType | StatusMessageCategory)[]
): [DeliveryLogType[], StatusMessageCategory[]] =>
    arrayToSplit?.reduce(
        ([statuses, messageCategory], el) => {
            if (Object.keys(LogStatusesToDisplay).includes(el)) {
                return [[...statuses, el], messageCategory];
            }
            return [statuses, [...messageCategory, el]];
        },
        [[], []]
    );

export const convertMessageCategoryAndStatuses = (
    statuses: DeliveryLogType[],
    selectedResult: StatusMessageCategory[],
    onlyLastEvent = false
): [DeliveryLogType[], StatusMessageCategory[]] => {
    const [updatedStatuses, updatedMessageCategories] = removeStatusFromMessageCategory(
        statuses || [],
        selectedResult || [],
        onlyLastEvent
    );

    const castedStatuses = flatten(
        updatedStatuses?.map((singleStatus) => filterStatusesToDeliveryLogTypees[singleStatus])
    );

    return [castedStatuses, updatedMessageCategories];
};

export const canSeeExtendedLogs = (AccountAccessLevel: AccessLevel[]): boolean =>
    AccountAccessLevel?.includes(AccessLevel.ExtendedLogs);
